import { lazy, Suspense, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "react-redux";
import { track } from "./analytics/track";
import { EVENTS } from "./analytics/events";
import ThemeToastContainer from "./components/UI/ThemeToastContainer/ThemeToastContainer";
import { store } from "./store/store";
import { routes } from "./routes/routes";
import "./App.css";
// react-toastify's stylesheet used to load here. Toasts are now the design
// system's own `.dsk-toast`, which reads semantic variables and is therefore
// already correct in both themes with nothing passed to it.
import Intercom from "./components/Intercom/Intercom";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import CommandPalette from "./components/CommandPalette/CommandPalette";
import { GuestFavouriteMerge } from "./favourites/useGuestFavouriteMerge";
import { ScrollToTop } from "./routes/ScrollToTop";

import NotFound from "./components/NotFound/NotFound";
import Login from "./components/Login/login";
import SignUp from "./components/Signup/Signup";
import ForgetPassword from "./components/ForgetPassword/ForgetPassword";
import OtpVerification from "./components/OtpVerification/OtpVerification";
import ResetPassword from "./components/ResetPassword/ResetPassword";
import EmailVerification from "./components/EmailVerification/EmailVerification";
import EmailVerificationOtp from "./components/EmailVerificationOtp/EmailVerificationOtp";
import RegistrationComplete from "./components/RegistrationComplete/RegistrationComplete";
import SellerRegistration from "./components/SellerRegistration/SellerRegistration";
import LandingPage from "./components/LandingPage/LandingPage";
import ProtectedRoute from "./ProtectedRoute/ProtectedRoute";

import Marketplace from "./components/Marketplace/Marketplace";
import CardDetail from "./components/Marketplace/CardDetail/CardDetail";
import ListDetail from "./components/Marketplace/ListDetail/CardDetail";
import SellerStore from "./components/SellerStore/SellerStore";

import CreateListing from "./components/SellerDashboard/CreateListing/CreateListing";

// Import Admin components
import AdminLogin from "./AdminDashboard/AdminLogin/AdminLogin";
import AdminAccessCode from "./AdminDashboard/AdminLogin/AdminAccessCode";
import AdminDashboard from "./AdminDashboard/pages/AdminDashboard/AdminDashboard";
import AdminSellers from "./AdminDashboard/pages/AdminSellers/AdminSellers";
import AdminBuyers from "./AdminDashboard/pages/AdminBuyers/AdminBuyers";
import AdminPayouts from "./AdminDashboard/pages/AdminPayouts/AdminPayouts";
import AdminOrderTracking from "./AdminDashboard/pages/AdminOrderTracking/AdminOrderTracking";
import AdminUsers from "./AdminDashboard/pages/AdminUsers/AdminUsers";
import SystemSettings from "./AdminDashboard/components/SystemSettings/SystemSettings";

import { CartProvider, useCart } from "./Contexts/CartContext";
import CartOverlay from "./components/Cart/CartOverlay/CartOverlay";
import { ThemeProvider, useTheme } from "./Contexts/ThemeContext";
import CartPage from "./components/Cart/CartPage/CartPage";
import MarketPlaceDashbord from "./AdminDashboard/pages/MarketPlaceDashboard/MarketPlaceDashbord";
import Checkout from "./components/Checkout/Checkout";
import OrderTracking1 from "./pages/OrderTracking/OrderTracking1/OrderTracking1";
import PaymentSuccess from "./pages/PaymentSuccess/PaymentSuccess";
import PaymentFailed from "./pages/PaymentFailed/PaymentFailed";
import Profile from "./components/Profile/Profile";
import Collection from "./components/Wishlist";
import CreateLabel from "./pages/CreateLabel/CreateLabel";
import Dashboard from "./components/Dashboard/Dashboard";
import HomeRedirect from "./components/HomeRedirect/HomeRedirect";
import SetsPage from "./pages/Sets/SetsPage";
import TCGSetsPage from "./pages/Sets/TCGSetsPage";
import SetCardsPage from "./pages/Sets/SetCardsPage";

// The design-system gallery and explorer are lazy on purpose, and the reason
// is not code-splitting for its own sake. Their routes below are already
// gated on NODE_ENV, but a STATIC import puts the modules and their demo
// fixtures into main.js no matter what the router does with them - the guard
// removes the route, not the bytes. Phase 4 of the design-preview audit
// measured exactly this: `Master Ball Collectibles` was still shipping 5x in
// the production bundle after the preview scaffolding was deleted, and these
// two imports were the source. `lazy` moves them into their own chunk, which
// production never requests. Do not turn these back into static imports.
const Gallery = lazy(() => import("./design-system/Gallery"));
const Explorer = lazy(() => import("./design-system/explorer/Explorer"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

function SessionTracker() {
  const location = useLocation();
  useEffect(() => {
    if (!sessionStorage.getItem("vaultsa_site_visited")) {
      sessionStorage.setItem("vaultsa_site_visited", "1");
      const params = new URLSearchParams(window.location.search);
      track(EVENTS.SITE_VISITED, {
        referrer: document.referrer || null,
        utm_source: params.get("utm_source"),
        utm_campaign: params.get("utm_campaign"),
        utm_medium: params.get("utm_medium"),
        landing_path: window.location.pathname,
      });
    }
  }, []);
  useEffect(() => {
    if (location.pathname === "/" || location.pathname === "/marketplace") {
      track(EVENTS.HOME_VIEWED, { path: location.pathname });
    }
  }, [location.pathname]);
  return null;
}

/**
 * Bridges `CartContext`'s drawer flag to the drawer itself.
 *
 * A separate component rather than inline in `App`, because `useCart()` has to
 * be called BELOW `CartProvider` and `App` is the thing that renders it.
 */
function GlobalCartDrawer() {
  const { showCartOverlay, setShowCartOverlay } = useCart();
  return (
    <CartOverlay isOpen={Boolean(showCartOverlay)} onClose={() => setShowCartOverlay(false)} />
  );
}

function App() {
  return (
    <ErrorBoundary name="Root">
      <Provider store={store}>
        <ThemeProvider>
          <CartProvider>
            <QueryClientProvider client={queryClient}>
              <div className="App">
                <Intercom appId="ltuv7rn3" />
                <ScrollToTop />
                <SessionTracker />
                <GuestFavouriteMerge />
                <Routes>
                  {/* Public Routes */}
              <Route
                path={routes.HOME}
                element={
                  <ErrorBoundary name="Marketplace">
                    <Marketplace />
                  </ErrorBoundary>
                }
              />
              <Route path={routes.LOGIN} element={<Login />} />
              <Route path={routes.SIGNUP} element={<SignUp />} />
              <Route path={routes.FORGET} element={<ForgetPassword />} />
              <Route
                path={routes.OTP_VERIFICATION}
                element={<OtpVerification />}
              />
              <Route
                path={routes.EMAIL_VERIFICATION}
                element={<EmailVerification />}
              />
              <Route
                path={routes.EMAIL_VERIFICATION_OTP}
                element={<EmailVerificationOtp />}
              />
              <Route
                path={routes.PAYMENTSUCCESS}
                element={<PaymentSuccess />}
              />
              <Route path={routes.PAYMENTFAILED} element={<PaymentFailed />} />
              <Route
                path={routes.PROFILE}
                element={
                  <ErrorBoundary name="Profile">
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              {/* The collection. Open to guests: a signed-out buyer's saved
                  cards live on this device via `useFavourites`, and the page
                  now renders that list (with a prompt to sign in and keep it)
                  instead of gating on a session that a guest doesn't have. */}
              <Route
                path={routes.COLLECTION}
                element={
                  <ErrorBoundary name="Collection">
                    <Collection />
                  </ErrorBoundary>
                }
              />
              <Route path={routes.RESET_PASSWORD} element={<ResetPassword />} />
              <Route path={routes.RESET_PASSWORD_WITH_TOKEN} element={<ResetPassword />} />
              <Route
                path={routes.REGISTRATION_COMPLETE}
                element={<RegistrationComplete />}
              />
              <Route path={routes.CART} element={<CartPage />} />
              {/* Protected Routes */}
              <Route
                path={routes.SELLER_REGISTRATION}
                element={
                  <ProtectedRoute>
                    <SellerRegistration />
                  </ProtectedRoute>
                }
              />
              <Route
                path={routes.DASHBOARD}
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path={routes.MARKETPLACE_DASHBOARD}
                element={
                  <ProtectedRoute>
                    <MarketPlaceDashbord />
                  </ProtectedRoute>
                }
              />
              {/* Create Listing - Seller Only */}
              <Route
                path={routes.CREATE_LISTING}
                element={
                  <ProtectedRoute requireSeller={true}>
                    <CreateListing />
                  </ProtectedRoute>
                }
              />
              <Route
                path={routes.ORDER_TRACKING}
                element={
                  // <ProtectedRoute>
                  <OrderTracking1 />
                  // </ProtectedRoute>
                }
              />
              <Route
                path={routes.CREATE_LABEL}
                element={
                  <ProtectedRoute requireSeller={true}>
                    <CreateLabel />
                  </ProtectedRoute>
                }
              />
              {/* Admin Routes */}
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route path="/admin-login/access" element={<AdminAccessCode />} />
              <Route
                path="/admin-dashboard"
                element={
                  <ErrorBoundary name="Admin">
                    <ProtectedRoute requireAdmin={true}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin-sellers"
                element={
                  <ErrorBoundary name="Admin">
                    <ProtectedRoute requireAdmin={true}>
                      <AdminSellers />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin-buyers"
                element={
                  <ErrorBoundary name="Admin">
                    <ProtectedRoute requireAdmin={true}>
                      <AdminBuyers />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin-users"
                element={
                  <ErrorBoundary name="Admin">
                    <ProtectedRoute requireAdmin={true}>
                      <AdminUsers />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin-payouts"
                element={
                  <ErrorBoundary name="Admin">
                    <ProtectedRoute requireAdmin={true}>
                      <AdminPayouts />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin-order-tracking"
                element={
                  <ErrorBoundary name="Admin">
                    <ProtectedRoute requireAdmin={true}>
                      <AdminOrderTracking />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path={routes.ADMIN_SYSTEM_SETTINGS}
                element={
                  <ErrorBoundary name="Admin">
                    <ProtectedRoute requireAdmin={true}>
                      <SystemSettings />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path={routes.CHECKOUT}
                element={
                  <ErrorBoundary name="Checkout">
                    <ProtectedRoute>
                      <Checkout />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path={routes.MARKETPLACE}
                element={
                  <ErrorBoundary name="Marketplace">
                    <Marketplace />
                  </ErrorBoundary>
                }
              />
              {/* Search results ARE the browse surface with a query applied:
                  same grid, same facets, same pagination, so a buyer can
                  narrow a search exactly the way they narrow a browse. The
                  only differences are where the query comes from (`?q=`) and
                  what the page calls itself. */}
              <Route
                path={routes.SEARCH}
                element={
                  <ErrorBoundary name="Search">
                    <Marketplace variant="search" />
                  </ErrorBoundary>
                }
              />
              <Route
                path={routes.SETS}
                element={
                  <ErrorBoundary name="Marketplace">
                    <SetsPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path={routes.SETS_CATEGORY}
                element={
                  <ErrorBoundary name="Marketplace">
                    <TCGSetsPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path={routes.SETS_CATEGORY_SET}
                element={
                  <ErrorBoundary name="Marketplace">
                    <SetCardsPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path={routes.SELLER_STORE}
                element={
                  <ErrorBoundary name="Marketplace">
                    <SellerStore />
                  </ErrorBoundary>
                }
              />
              <Route
                path={routes.CARD_DETAIL}
                element={
                  <ErrorBoundary name="Marketplace">
                    <CardDetail />
                  </ErrorBoundary>
                }
              />
              <Route
                path={routes.LISTING_DETAILS}
                element={
                  <ErrorBoundary name="Marketplace">
                    <ListDetail />
                  </ErrorBoundary>
                }
              />
              {/* Design system gallery. Development only, never shipped.
                  It is the smoke test for the component library and the surface
                  for reviewing the whole system in both themes and both
                  viewport tracks without clicking through every route. */}
              {process.env.NODE_ENV !== "production" && (
                <Route
                  path="/__design-system"
                  element={
                    <Suspense fallback={null}>
                      <Gallery />
                    </Suspense>
                  }
                />
              )}
              {/* Screen and state explorer. Development only, never shipped.
                  The gallery above proves the COMPONENTS render; this proves the
                  SCREENS do, in every state they can reach, with no API. The
                  screen itself renders at ?frame=1, which is what the explorer's
                  iframe loads so the mobile track gets a real viewport. */}
              {process.env.NODE_ENV !== "production" && (
                <>
                  <Route
                    path="/__design-system/screens"
                    element={
                      <Suspense fallback={null}>
                        <Explorer />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/__design-system/screens/:screenId"
                    element={
                      <Suspense fallback={null}>
                        <Explorer />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/__design-system/screens/:screenId/:stateId"
                    element={
                      <Suspense fallback={null}>
                        <Explorer />
                      </Suspense>
                    }
                  />
                </>
              )}
              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>

            {/* The command palette, mounted ONCE, for the same reason as the
                drawer below: Cmd-K has to work on every page, and a palette
                mounted per page would bind the shortcut as many times as there
                are shells on screen. It renders nothing until summoned. */}
            <CommandPalette />

            {/* The cart drawer, mounted ONCE for the whole app.
                It has to live above the routes because it outlives any single
                page: you open it from the header on marketplace, adjust a
                quantity, close it and you are still on marketplace. Before the
                conversion `SellerDashboard/NavigationHeader` mounted it
                globally in exactly this way; that file was deleted with the
                legacy chrome and nothing took over the mount, so the drawer
                existed but could not be opened from anywhere in the app.
                `CartButton` in the shell header is what opens it. */}
            <GlobalCartDrawer />

            {/* Toast Container */}
            <ThemeToastContainer />
            </div>
            </QueryClientProvider>
          </CartProvider>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
